/**
 * authTokenStore — Singleton store for non-React token access.
 *
 * React modules (components, hooks) should use the Logto SDK directly.
 * Non-React code (e.g. utility functions, shared helpers) can call
 * getAuthToken() after AuthContext registers the provider via useEffect.
 */

import { getAccessToken } from '../utils/tokenStore';

type TokenProvider = () => string | null;
type TokenRefresher = () => Promise<string | null>;

let tokenProvider: TokenProvider | null = null;
let tokenRefresher: TokenRefresher | null = null;
// In-flight refresh promise — dedupes concurrent 401s so N parallel failed
// requests share ONE refresh call instead of triggering N refreshes.
let refreshInFlight: Promise<string | null> | null = null;

/**
 * Replace the current token provider callback.
 *
 * Called once from AuthContext after useLogto() has been destructured,
 * so that the provider reads the React-internal token instead of
 * falling back to localStorage.
 */
export function setAuthTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

/**
 * Clear the stored provider (for unmount cleanup).
 */
export function clearAuthTokenProvider(): void {
  tokenProvider = null;
}

/**
 * Register an async refresher that returns a fresh access token.
 *
 * Called once from AuthContext alongside setAuthTokenProvider. The refresher
 * typically wraps Logto's useLogto().getAccessToken(), which auto-refreshes
 * expired tokens via the SDK's refresh-token flow.
 *
 * Unlike the sync provider (safe during render), the refresher is only
 * invoked from network-failure paths (e.g. a 401 interceptor), never during
 * React render.
 */
export function setAuthTokenRefresher(refresher: TokenRefresher): void {
  tokenRefresher = refresher;
}

export function clearAuthTokenRefresher(): void {
  tokenRefresher = null;
  refreshInFlight = null;
}

/**
 * Force-refresh the access token. Concurrent calls share one in-flight
 * refresh; subsequent calls wait on the same Promise.
 *
 * Falls back to the sync getAuthToken() when no refresher is registered
 * (e.g. unit tests, non-React contexts).
 */
export async function refreshAuthToken(): Promise<string | null> {
  if (!tokenRefresher) {
    return getAuthToken();
  }
  if (!refreshInFlight) {
    refreshInFlight = tokenRefresher().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * Get the current access token string, or null if unavailable.
 *
 * 1. Try the registered React provider (set via setAuthTokenProvider).
 * 2. Fall back to localStorage for development / non-React contexts.
 */
export function getAuthToken(): string | null {
  // First: try the registered provider (React-managed, Logto SDK-driven)
  if (tokenProvider) {
    const token = tokenProvider();
    if (token) return token;
  }

  // Fallback for non-React contexts (cookie first, then localStorage)
  return getAccessToken();
}
